import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useConsent } from "~/contexts/consent";
import {
  type TrackingParameters,
  toTrackingSearchParams,
  trackingParametersSchema,
} from "~/lib/shared/tracking-parameters";
import {
  readStoredTrackingParameters,
  syncStoredTrackingParameters,
} from "~/lib/tracking-parameters-storage";

const isBrowser = typeof window !== "undefined";

export interface TrackingContextValue {
  readonly parameters: TrackingParameters;
  readonly searchParams: URLSearchParams;
}

const TrackingContext = createContext<TrackingContextValue | null>(null);

export function TrackingProvider({
  children,
}: {
  readonly children: ReactNode;
}): React.ReactElement {
  const location = useLocation();
  const { allowed } = useConsent();
  const [parameters, setParameters] = useState<TrackingParameters>(() =>
    isBrowser && allowed.analytics ? readStoredTrackingParameters() : {},
  );

  // Campaign tags are kept in memory for every visitor, and stored between
  // visits only with analytics consent. Without it the stored copy is removed.
  useEffect(() => {
    syncStoredTrackingParameters(parameters, allowed.analytics);
  }, [parameters, allowed.analytics]);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const searchString =
      typeof location.search === "string"
        ? location.search
        : window.location.search;
    const params = new URLSearchParams(searchString);

    setParameters((previous) => {
      const accumulated: Record<string, string> = { ...previous };
      let changed = false;

      params.forEach((value, key) => {
        if (accumulated[key] !== value) {
          accumulated[key] = value;
          changed = true;
        }
      });

      const parsed = trackingParametersSchema.parse(accumulated);

      const sameSize =
        Object.keys(parsed).length === Object.keys(previous).length;
      const sameContent =
        sameSize &&
        Object.entries(parsed).every(([key, value]) => previous[key] === value);

      if (!changed && sameContent) {
        return previous;
      }

      return parsed;
    });
  }, [location.search]);

  const value = useMemo<TrackingContextValue>(() => {
    const searchParams = toTrackingSearchParams(parameters);

    return {
      parameters,
      searchParams,
    };
  }, [parameters]);

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
}

export function useTracking(): TrackingContextValue {
  const context = useContext(TrackingContext);
  if (context === null) {
    throw new Error("useTracking must be used within TrackingProvider");
  }

  return context;
}
